import * as React from 'react'
import produce from 'immer'
import { Form, Input, Spin } from 'antd'
import { isEqual, isEmpty, get, set, debounce } from 'lodash'
import { showError, showClientNotifications, showValidationError, ValidationError } from 'helpers/errors'
import { createLabelFactory, createFieldFactory, isReadOnly } from 'helpers/formViews'
import { Emitter, stopEvent } from 'helpers/events'
import { FORM_SUBMIT_EVENT } from 'options/events'
import splitTypes from 'options/cycleCounts/cycleCountSplitTypes'
import { t } from 'helpers/i18n'
import { DEBOUNCE } from 'helpers/utils'
import Select, { Option } from 'elements/Select'
import InputNumber from 'elements/InputNumber'

class FormView extends React.Component {
  state = {
    item: {
      splitType: 'ByCounts',
      splitQuantity: null,
    },
  }

  constructor(props) {
    super(props)

    this.fetchSplitDistributionText = debounce(this.fetchSplitDistributionText, DEBOUNCE)
  }

  componentDidMount() {
    this.fetchSettings()

    Emitter.on(FORM_SUBMIT_EVENT, this.handleSubmit)
  }

  componentWillUnmount() {
    Emitter.off(FORM_SUBMIT_EVENT, this.handleSubmit)
  }

  componentDidUpdate(prevProps) {
    if (!isEqual(prevProps.parentRecord, this.props.parentRecord)) {
      this.fetchSettings()
    }
  }

  fetchSettings = async () => {
    try {
      const fieldSettings = await this.props
        .getSettings({ type: 'splitCycleCount' })
        .then((r) => get(r, 'value.data.fieldSettings', []))

      this.setState({ fieldSettings })
    } catch (error) {
      showError({ error })
    }
  }

  fetchSplitDistributionText = async () => {
    const { splitType, splitQuantity } = this.state.item ?? {}

    if (splitQuantity > 0) {
      try {
        const response = await this.props
          .getSplitDistributionText({
            id: this.props.parentRecord.id,
            splitType,
            splitQuantity,
          })
          .then((r) => r.value.data)

        this.setState(
          produce((draft) => {
            draft.item.splitQuantity = response.splitQuantity
            draft.splitDistributionText = response.splitDistributionText
          }),
          () => this.props.form.setFieldsValue({ splitQuantity: response.splitQuantity })
        )
      } catch (error) {
        showError({ error })
      }
    } else {
      this.setState({ splitDistributionText: null })
    }
  }

  handleSubmit = (e) => {
    const { splitType, splitQuantity } = this.state.item ?? {}

    stopEvent(e)

    this.props.form.validateFields(async (errors, values) => {
      if (!isEmpty(errors)) {
        showValidationError()
        return
      }

      try {
        await this.props.onSave?.(true)

        const response = await this.props.splitCycleCount({
          id: this.props.parentRecord.id,
          splitType,
          splitQuantity,
        })

        showClientNotifications({ response })

        if (response.value.data.failureCount > 0) {
          throw new Error()
        }

        await this.props.onSave?.(false)
        await this.props.onCancel?.(true)
      } catch (error) {
        showError({ error })
        await this.props.onSave?.(false)
      }
    })
  }

  setItemValue = (name, value) =>
    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      }),
      this.fetchSplitDistributionText
    )

  render() {
    const { item, fieldSettings } = this.state

    if (!item || !fieldSettings) {
      return <Spin />
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this)

    return (
      <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
        <ValidationError errors={this.state.validationErrors} />
        <Form.Item label={createLabel('itemsOnCount')}>
          <Input value={this.props.parentRecord.itemsOnCount} disabled />
        </Form.Item>
        <Form.Item label={createLabel('splitType')}>
          {createFieldDecorator('splitType')(
            <Select onChange={(value) => this.setItemValue('splitType', value)} allowClear={false}>
              {Object.entries(splitTypes).map(([key, value]) => (
                <Option key={key} value={key}>
                  {t(value)}
                </Option>
              ))}
            </Select>
          )}
        </Form.Item>
        <Form.Item label={t(item.splitType === 'ByItems' ? 'numberOfItemsPerCount' : 'numberOfCounts')}>
          {createFieldDecorator('splitQuantity')(
            <InputNumber
              onChange={(value) => this.setItemValue('splitQuantity', Math.max(value, 1))}
              min={1}
              allowClear
            />
          )}
        </Form.Item>
        {!isEmpty(this.state.splitDistributionText) && <p>{this.state.splitDistributionText}</p>}
      </Form>
    )
  }
}

export default Form.create()(FormView)
